import { MqlCompiledQuery } from '@deqo/mql-js';
import { injectable } from 'inversify';
import { throttle } from 'throttle-debounce';
import { ObjectManager } from '../../../../concepts/objects';
import { UniversalSurfaceRenderer } from '../../../../platform/UniversalSurfaceRenderer';
import { ActiveObject, resolve } from '../../../Container';
import { Camera } from '../../../canvas/Camera';
import { Physical, RelativeTo } from '../../../canvas/entities/Physical';
import { OverlayHandle } from '../../../canvas/overlay/Overlay';
import { OverlayManager } from '../../../canvas/overlay/OverlayManager';
import { Lens } from '../Lens';
import { MqlUtils } from '../MqlUtils';

@injectable()
export class HighlightLens extends ActiveObject implements Lens {

    @resolve(Camera) private camera: Camera;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(OverlayManager) private overlays: OverlayManager;
    @resolve(MqlUtils) private mqlUtils: MqlUtils;

    private filter = null as MqlCompiledQuery;
    private activeSet = new Set<Physical>();
    private handle = null as OverlayHandle;

    public configure(filter: MqlCompiledQuery): void {
        this.filter = filter;
    }

    public start() {
        const applyThrottled = throttle(0, this.apply.bind(this), true);
        this.handle = this.overlays.createOverlay('highlight-lens', this.onOverlayPaint);
        this.overlays.add(this.handle);
        this.burden(
            this.handle,
            () => this.overlays.remove(this.handle),
            this.objects.on('updated', applyThrottled),
            this.camera.observe(() => this.handle?.invalidate()),
        );
        this.apply();
    }

    private apply() {
        const scope = this.mqlUtils.createScope();
        const elmts = Array.from(this.objects.getAll(Physical));
        this.activeSet = new Set(elmts.filter(x => this.filter.test(scope, x)));
        this.handle?.invalidate();
    }

    private onOverlayPaint = (gfx: UniversalSurfaceRenderer) => {

        gfx.setTransform(1, 0, 0, 1, 0, 0);

        gfx.globalAlpha = 0.5;
        gfx.fillStyle = '#000';
        gfx.fillRect(0, 0, gfx.width, gfx.height);

        gfx.globalAlpha = 1;
        gfx.globalCompositeOperation = 'destination-out';

        gfx.setTransform(this.camera.modelViewMatrix);

        for (const ph of this.activeSet) {
            const bb = ph.bounds(RelativeTo.Model).inflate(20);
            gfx.fillRoundRect(bb.left, bb.top, bb.width, bb.height, 10);
        }
    };
}
